import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { BfAuthService } from '@brafton/skynet-angular-security-service';
import { FeedbackApiTokenStore } from '@brafton/feedback-api';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { StylesTopBarComponent } from '@brafton/angular-styles-library';

import { SideMenu } from '../../components/side-menu/side-menu';
import { CurrentUserService } from '../../core/current-user.service';

/**
 * Application shell: the org styles library's `styles-card-top-bar` spanning the
 * full width, with {@link SideMenu} beneath it — the nav rail, which also hosts the
 * routed page in its drawer content.
 *
 * The library components are standalone in this build, so they are imported directly
 * rather than through `BraftonStylesLibraryModule`.
 *
 * The top-bar breadcrumb is derived from the active route's `data.breadcrumbs`
 * and recomputed on every navigation.
 */
@Component({
  selector: 'app-shell',
  imports: [
    RouterOutlet,
    MatIconModule,
    MatMenuModule,
    MatDividerModule,
    StylesTopBarComponent,
    SideMenu,
  ],
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Shell {
  private readonly router = inject(Router);
  private readonly auth = inject(BfAuthService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly apiTokenStore = inject(FeedbackApiTokenStore);

  /**
   * Collapsed state of the nav rail. Collapsing narrows it to icons only rather
   * than closing it, so the drawer itself stays open — see the `icon-menu` class in
   * the template.
   */
  protected readonly railCollapsed = signal(false);

  /** Display name of the signed-in user, for the account menu. */
  protected readonly displayName = this.currentUser.displayName;

  /** Profile picture URL from the Auth0 token; null falls back to an icon. */
  protected readonly avatarUrl = this.currentUser.pictureUrl;

  /** Breadcrumb trail for the active route; updates on each navigation. */
  protected readonly breadcrumbs = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.resolveBreadcrumbs()),
    ),
    { initialValue: [] as string[] },
  );

  toggleRail(): void {
    this.railCollapsed.update((collapsed) => !collapsed);
  }

  /**
   * Ends the session. The org auth library clears its localStorage tokens and
   * then hands off to Auth0's logout endpoint, which redirects the browser to
   * `origin + LogoutURL` (i.e. `/login`) — so this navigates away and no code
   * after it is guaranteed to run. Unlike the previous in-app-only logout, this
   * DOES clear the Auth0 SSO cookie, so the next login re-prompts.
   */
  logOut(): void {
    // Drop the Feedback API token so a logged-out user retains no API access and
    // the auth guard fetches a fresh token on the next login.
    this.apiTokenStore.clear();
    this.auth.logout();
  }

  /** Walks the live router state and returns the deepest `data.breadcrumbs`. */
  private resolveBreadcrumbs(): string[] {
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let breadcrumbs: string[] = [];
    while (snapshot) {
      const fromRoute = snapshot.data['breadcrumbs'] as string[] | undefined;
      if (fromRoute?.length) {
        breadcrumbs = fromRoute;
      }
      snapshot = snapshot.firstChild;
    }
    return breadcrumbs;
  }
}
